/**
 * JPEG / PNG / WebP / GIF / AVIF decode via the browser's native
 * createImageBitmap. CRITICAL: `imageOrientation: 'from-image'` bakes EXIF
 * orientation so width/height and the preview are display-correct.
 *
 * Older browsers (Safari before about 16.4, e.g. a Monterey Mac) only know the
 * old 'none' | 'flipY' values and reject 'from-image', so EVERY photo failed
 * with "We couldn't read this file" (Gorgias, Sophie, 19 Oct 2026). On any
 * failure we retry through an <img> element, which every browser decodes with
 * EXIF orientation applied, then hand back a bitmap of that.
 */
export async function decodeRaster(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (err) {
    if (typeof document === 'undefined') throw err;
    try {
      return await decodeViaImageElement(file);
    } catch {
      throw err; // keep the native error for the upload error log
    }
  }
}

export async function decodeViaImageElement(file: Blob): Promise<ImageBitmap> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    if (typeof img.decode === 'function') {
      await img.decode();
    } else {
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('image element could not load the file'));
      });
    }
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) throw new Error('image element decoded to zero size');
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D context unavailable');
    ctx.drawImage(img, 0, 0);
    if (typeof createImageBitmap === 'function') return await createImageBitmap(canvas);
    // No createImageBitmap at all (Safari 14): a canvas has width/height and
    // draws anywhere a bitmap does, which is all the preview step needs.
    return Object.assign(canvas, { close() {} }) as unknown as ImageBitmap;
  } finally {
    URL.revokeObjectURL(url);
  }
}
